import React, { useCallback, useEffect, useRef } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { type GolemErrorState } from "@/lib/golem/useGolemErrors";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

type FieldProps = {
    label?: string;
    required?: boolean;
    error?: string;
    children: React.ReactNode;
};

function FieldWrapper({ label, required, error, children }: FieldProps) {
    return (
        <div className="flex flex-col gap-1.5">
            {label && (
                <label className={`text-xs font-medium uppercase tracking-wide ${error ? "text-error-red" : "text-dark-grey"}`}>
                    {label}{required && <span className="ml-0.5 text-blue/70">*</span>}
                </label>
            )}
            {children}
            {error && (
                <p className="text-xs text-error-red" data-field-error>{error.charAt(0).toUpperCase() + error.slice(1)}</p>
            )}
        </div>
    );
}

const inputBorder = (hasError: boolean) =>
    `w-full border rounded-lg px-3 py-2.5 text-sm text-dark-dark-grey placeholder:text-light-grey bg-white
     focus:outline-none focus:ring-2 transition-colors
     disabled:opacity-50 disabled:cursor-not-allowed
     ${hasError
        ? "border-error-red/50 focus:ring-error-red/20 focus:border-error-red/70"
        : "border-light-grey hover:border-grey focus:ring-blue/20 focus:border-blue"}`;

type FieldInputProps = {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    errors: GolemErrorState;
    field: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    type?: string;
};

export function FieldInput({ label, value, onChange, errors, field, placeholder, required, disabled, type = "text" }: FieldInputProps) {
    const error = errors.fieldError(field);

    const handleChange = useCallback((val: string) => {
        errors.clearField(field);
        onChange(val);
    }, [onChange, errors, field]);

    return (
        <FieldWrapper label={label} required={required} error={error}>
            <input
                type={type}
                value={value}
                onChange={e => handleChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                className={inputBorder(!!error)}
            />
        </FieldWrapper>
    );
}

type FieldTextAreaProps = {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    errors: GolemErrorState;
    field: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    rows?: number;
    maxRows?: number;
};

export function FieldTextArea({ label, value, onChange, errors, field, placeholder, required, disabled, rows = 3, maxRows }: FieldTextAreaProps) {
    const error = errors.fieldError(field);
    const ref = useRef<HTMLTextAreaElement>(null);

    const handleChange = useCallback((val: string) => {
        errors.clearField(field);
        onChange(val);
    }, [onChange, errors, field]);

    useEffect(() => {
        if (!maxRows || !ref.current) return;
        const el = ref.current;
        el.style.height = "auto";
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
        const paddingY = parseFloat(getComputedStyle(el).paddingTop) + parseFloat(getComputedStyle(el).paddingBottom);
        const maxHeight = lineHeight * maxRows + paddingY;
        el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
        el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
    }, [value, maxRows]);

    return (
        <FieldWrapper label={label} required={required} error={error}>
            <textarea
                ref={ref}
                value={value}
                onChange={e => handleChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                rows={rows}
                className={`${inputBorder(!!error)} resize-none`}
            />
        </FieldWrapper>
    );
}

type FieldButtonProps = {
    text: string;
    onClick?: () => void;
    loading?: boolean;
    disabled?: boolean;
    variant?: "primary" | "secondary" | "danger" | "danger-outline";
};

export function FieldButton({ text, onClick, loading = false, disabled = false, variant = "primary" }: FieldButtonProps) {
    const base = "w-full h-10 rounded-lg text-sm font-medium transition-colors flex items-center justify-center disabled:cursor-not-allowed";
    const colours = {
        primary:         "bg-blue text-white hover:bg-dark-blue disabled:bg-light-grey",
        secondary:       "bg-white text-grey border-2 border-light-light-grey hover:text-dark-grey disabled:opacity-50",
        danger:          "bg-error-red text-white hover:bg-dark-red disabled:bg-light-grey",
        "danger-outline": "bg-white text-error-red border-2 border-error-red hover:text-dark-red hover:border-dark-red disabled:opacity-50",
    };
    const spinnerWhite = variant === "primary" || variant === "danger";

    return (
        <button
            type="button"
            className={`${base} ${colours[variant]}`}
            onClick={onClick}
            disabled={disabled || loading}
        >
            {loading
                ? <LoadingSpinner width={16} height={16} color={spinnerWhite ? "white" : undefined} />
                : text}
        </button>
    );
}

const sliderClass = `w-full cursor-pointer appearance-none h-1.5 rounded-full bg-light-light-grey
    [&::-webkit-slider-thumb]:appearance-none
    [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
    [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue
    [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm
    [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110
    [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4
    [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue
    [&::-moz-range-thumb]:cursor-pointer`;

type FieldSliderProps = {
    label: string;
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    step?: number;
    disabled?: boolean;
    formatValue?: (value: number) => string;
    minLabel?: string;
    maxLabel?: string;
};

export function FieldSlider({
    label, value, onChange, min, max, step = 1,
    disabled = false, formatValue, minLabel, maxLabel,
}: FieldSliderProps) {
    const display = formatValue ? formatValue(value) : String(value);
    return (
        <div className={`flex flex-col gap-2 transition-opacity ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-dark-grey uppercase tracking-wide">{label}</span>
                <span className="text-sm font-semibold text-dark-dark-grey bg-desaturated-grey px-2.5 py-0.5 rounded-full tabular-nums">
                    {display}
                </span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                disabled={disabled}
                onChange={e => onChange(parseInt(e.target.value, 10))}
                className={sliderClass}
            />
            {(minLabel !== undefined || maxLabel !== undefined) && (
                <div className="flex justify-between text-xs text-light-grey select-none">
                    <span>{minLabel ?? min}</span>
                    <span>{maxLabel ?? max}</span>
                </div>
            )}
        </div>
    );
}

type FieldSectionProps = {
    title: string;
    children: React.ReactNode;
    first?: boolean;
};

export function FieldSection({ title, children, first = false }: FieldSectionProps) {
    return (
        <div className={`flex flex-col gap-5 ${first ? "" : "pt-5 border-t border-light-light-grey"}`}>
            <h3 className="text-sm font-semibold text-dark-dark-grey">{title}</h3>
            {children}
        </div>
    );
}

type FieldJSONProps = {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    errors: GolemErrorState;
    field: string;
    required?: boolean;
    minHeight?: string;
    showPaths?: boolean;
};

export function FieldJSON({ label, value, onChange, errors, field, required, minHeight = "120px", showPaths }: FieldJSONProps) {
    const error = errors.fieldError(field);
    const childErrors = showPaths ? errors.fieldChildErrors(field) : [];
    const hasError = !!error || childErrors.length > 0;

    const handleChange = useCallback((val: string) => {
        errors.clearField(field);
        onChange(val);
    }, [onChange, errors, field]);

    return (
        <FieldWrapper label={label} required={required} error={showPaths ? undefined : error}>
            <div className={`rounded-lg overflow-hidden border max-h-64 overflow-y-auto transition-colors
                [&_.cm-scroller]:overflow-x-auto [&_.cm-scroller::-webkit-scrollbar]:h-1.5
                [&_.cm-scroller::-webkit-scrollbar-track]:bg-transparent
                [&_.cm-scroller::-webkit-scrollbar-thumb]:bg-light-grey [&_.cm-scroller::-webkit-scrollbar-thumb]:rounded-full
                ${hasError ? "border-error-red/50" : "border-light-grey hover:border-grey"}`}
            >
                <CodeMirror
                    value={value}
                    extensions={[json()]}
                    onChange={handleChange}
                    minHeight={minHeight}
                    basicSetup={{ lineNumbers: true, foldGutter: false }}
                />
            </div>
            {showPaths && childErrors.length > 0 && (
                <ul className="flex flex-col gap-0.5" data-field-error>
                    {childErrors.map(({ path, message }) => (
                        <li key={path} className="text-xs text-error-red">
                            <span className="font-mono">{path}</span>: {message.charAt(0).toUpperCase() + message.slice(1)}
                        </li>
                    ))}
                </ul>
            )}
            {showPaths && !childErrors.length && error && (
                <p className="text-xs text-error-red" data-field-error>{error.charAt(0).toUpperCase() + error.slice(1)}</p>
            )}
        </FieldWrapper>
    );
}
